import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { PRODUCT_TYPES, SHOPIFY_ADMIN_URL } from '../../utils/constants'
import {
  EditableCell,
  EditablePriceCell,
  CategoryDropdown,
  SearchLinks,
  MetricsRow,
  ProgressBar,
  DraftStatusBadge,
  PricingResultsModal
} from '../shared'

function DraftsManager() {
  const [drafts, setDrafts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selected, setSelected] = useState(new Set())
  const [activating, setActivating] = useState(false)
  const [pricing, setPricing] = useState(false)
  const [pricingProgress, setPricingProgress] = useState({ current: 0, total: 0, status: '' })
  const [filter, setFilter] = useState('all')
  const [sortBy, setSortBy] = useState('title')
  const [sortDir, setSortDir] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const [pendingChanges, setPendingChanges] = useState({})
  const [saving, setSaving] = useState(false)
  const [showPricingResults, setShowPricingResults] = useState(false)
  const [pricingResultsData, setPricingResultsData] = useState([])
  const [limit, setLimit] = useState(100)
  const abortControllerRef = useRef(null)

  const fetchDrafts = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/api/inventory/shopify/drafts')
      const enrichedDrafts = (response.drafts || []).map(d => ({
        ...d,
        sellPrice: parseFloat(d.price) || 0,
        costPrice: parseFloat(d.cost) || 0,
        isFlagged: parseFloat(d.price) === 0.01,
        isUnpriced: parseFloat(d.price) === 0,
        imageCount: d.imageCount || 0
      }))
      setDrafts(enrichedDrafts)
      setPendingChanges({})
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDrafts()
  }, [fetchDrafts])

  // Track changes to a draft
  const updateDraft = (id, field, value) => {
    setDrafts(drafts.map(d => {
      if (d.id === id) {
        const updated = { ...d, [field]: value }
        if (field === 'price') {
          updated.sellPrice = parseFloat(value) || 0
          updated.isFlagged = updated.sellPrice === 0.01
          updated.isUnpriced = updated.sellPrice === 0
        }
        if (field === 'cost') {
          updated.costPrice = parseFloat(value) || 0
        }
        return updated
      }
      return d
    }))

    setPendingChanges(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value
      }
    }))
  }

  // Save pending changes to Shopify
  const saveChanges = async () => {
    const changedIds = Object.keys(pendingChanges)
    if (changedIds.length === 0) return

    setSaving(true)
    try {
      for (const id of changedIds) {
        const changes = pendingChanges[id]
        await api.post(`/api/inventory/shopify/update/${id}`, changes)
      }
      setPendingChanges({})
      alert(`Saved changes to ${changedIds.length} product(s)`)
    } catch (err) {
      alert(`Error saving: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  const toggleSelect = (id) => {
    const newSelected = new Set(selected)
    if (newSelected.has(id)) newSelected.delete(id)
    else newSelected.add(id)
    setSelected(newSelected)
  }

  const selectAllVisible = () => {
    const visibleIds = filteredDrafts.map(d => d.id)
    const allSelected = visibleIds.every(id => selected.has(id))
    const newSelected = new Set(selected)
    if (allSelected) {
      visibleIds.forEach(id => newSelected.delete(id))
    } else {
      visibleIds.forEach(id => newSelected.add(id))
    }
    setSelected(newSelected)
  }

  const activateSelected = async () => {
    if (selected.size === 0) return

    // Validate selected drafts
    const selectedDrafts = drafts.filter(d => selected.has(d.id))
    const noPhotos = selectedDrafts.filter(d => !d.imageCount || d.imageCount === 0)
    const noType = selectedDrafts.filter(d => !d.product_type)

    if (noPhotos.length > 0 || noType.length > 0) {
      const issues = []
      if (noPhotos.length > 0) issues.push(`${noPhotos.length} item(s) have no photos`)
      if (noType.length > 0) issues.push(`${noType.length} item(s) have no product type`)
      alert(`Cannot activate:\n\n${issues.join('\n')}\n\nPlease fix these items before activating.`)
      return
    }

    setActivating(true)
    try {
      const result = await api.post('/api/inventory/shopify/activate', {
        productIds: Array.from(selected)
      })
      const pubInfo = result.published ? ` | Published to all channels: ${result.published}` : ''
      const pubErrors = result.publishErrors?.length ? ` | Publish errors: ${JSON.stringify(result.publishErrors)}` : ''
      alert(`Activated ${result.activated || selected.size} products${pubInfo}${pubErrors}`)
      setDrafts(drafts.filter(d => !selected.has(d.id)))
      setSelected(new Set())
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setActivating(false)
    }
  }

  const priceSelected = async () => {
    if (selected.size === 0) return

    // Check for missing categories
    const selectedIds = Array.from(selected)
    const missingCategory = selectedIds.filter(id => {
      const draft = drafts.find(d => d.id === id)
      return draft && !draft.product_type
    })

    if (missingCategory.length > 0) {
      const proceed = window.confirm(
        `${missingCategory.length} item(s) have no category set.\n\n` +
        `Items without a category may be priced incorrectly.\n\n` +
        `Do you want to continue anyway?`
      )
      if (!proceed) return
    }

    setPricing(true)
    setPricingProgress({ current: 0, total: selectedIds.length, status: 'Calculating prices...' })
    abortControllerRef.current = new AbortController()

    const pricingResults = []

    try {
      for (let i = 0; i < selectedIds.length; i++) {
        if (abortControllerRef.current.signal.aborted) break
        const productId = selectedIds[i]
        const draft = drafts.find(d => d.id === productId)
        const title = draft?.title || `Product ${productId}`
        const oldPrice = draft?.sellPrice || 0

        setPricingProgress({
          current: i + 1,
          total: selectedIds.length,
          status: `Calculating: ${title.substring(0, 40)}...`
        })

        try {
          const result = await api.post(`/api/inventory/shopify/price-draft/${productId}?dry_run=true`)
          const newPrice = result.sellPrice || 0

          pricingResults.push({
            id: productId,
            title: title,
            oldPrice: oldPrice,
            newPrice: newPrice,
            changed: oldPrice !== newPrice,
            flagged: newPrice === 0.01,
            selected: newPrice > 0 && newPrice !== 0.01,
            error: false
          })
        } catch (err) {
          pricingResults.push({
            id: productId,
            title: title,
            oldPrice: oldPrice,
            newPrice: null,
            changed: false,
            flagged: false,
            selected: false,
            error: true
          })
        }
      }

      setPricingResultsData(pricingResults)
      setShowPricingResults(true)
    } catch (err) {
      alert(`Error: ${err.message}`)
    } finally {
      setPricing(false)
      setPricingProgress({ current: 0, total: 0, status: '' })
    }
  }

  // Handle toggle in pricing results modal
  const handleTogglePricingResult = (id, forceValue) => {
    setPricingResultsData(prev => prev.map(r => {
      if (r.id === id) {
        return { ...r, selected: forceValue !== undefined ? forceValue : !r.selected }
      }
      return r
    }))
  }

  // Apply confirmed price changes
  const applyPricingChanges = async () => {
    const toApply = pricingResultsData.filter(r => r.selected && r.newPrice && r.newPrice > 0)
    if (toApply.length === 0) {
      setShowPricingResults(false)
      setSelected(new Set())
      return
    }

    setPricing(true)
    setPricingProgress({ current: 0, total: toApply.length, status: 'Applying changes...' })

    const updatedDrafts = [...drafts]
    let applied = 0

    for (const item of toApply) {
      setPricingProgress({
        current: applied + 1,
        total: toApply.length,
        status: `Updating: ${item.title.substring(0, 30)}...`
      })

      try {
        await api.post(`/api/inventory/shopify/update/${item.id}`, { price: item.newPrice })
        const draftIndex = updatedDrafts.findIndex(d => d.id === item.id)
        if (draftIndex !== -1) {
          updatedDrafts[draftIndex] = {
            ...updatedDrafts[draftIndex],
            sellPrice: item.newPrice,
            price: item.newPrice.toString(),
            isFlagged: item.newPrice === 0.01,
            isUnpriced: item.newPrice === 0
          }
        }
        applied++
      } catch (err) {
        console.error(`Failed to update ${item.id}:`, err)
      }

      await new Promise(resolve => setTimeout(resolve, 200))
    }

    setDrafts(updatedDrafts)
    setShowPricingResults(false)
    setSelected(new Set())
    setPricing(false)
    setPricingProgress({ current: 0, total: 0, status: '' })

    alert(`Applied ${applied} of ${toApply.length} price changes`)
  }

  const cancelPricing = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort()
    setPricing(false)
    setPricingProgress({ current: 0, total: 0, status: '' })
  }

  const allFilteredDrafts = useMemo(() => {
    return drafts
      .filter(d => {
        if (filter === 'priced' && (d.sellPrice === 0 || d.isFlagged)) return false
        if (filter === 'unpriced' && d.sellPrice !== 0) return false
        if (filter === 'flagged' && !d.isFlagged) return false
        if (filter === 'nocategory' && d.product_type) return false
        if (searchTerm) {
          const term = searchTerm.toLowerCase()
          return d.title?.toLowerCase().includes(term) || d.product_type?.toLowerCase().includes(term)
        }
        return true
      })
      .sort((a, b) => {
        let aVal, bVal
        if (sortBy === 'price') { aVal = a.sellPrice; bVal = b.sellPrice }
        else if (sortBy === 'cost') { aVal = a.costPrice; bVal = b.costPrice }
        else if (sortBy === 'type') { aVal = a.product_type || ''; bVal = b.product_type || '' }
        else if (sortBy === 'photos') { aVal = a.imageCount || 0; bVal = b.imageCount || 0 }
        else { aVal = a.title || ''; bVal = b.title || '' }
        if (typeof aVal === 'string') return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal
      })
  }, [drafts, filter, searchTerm, sortBy, sortDir])

  const filteredDrafts = useMemo(() => {
    return allFilteredDrafts.slice(0, limit)
  }, [allFilteredDrafts, limit])

  const stats = {
    total: drafts.length,
    priced: drafts.filter(d => d.sellPrice > 0.01).length,
    unpriced: drafts.filter(d => d.sellPrice === 0).length,
    flagged: drafts.filter(d => d.isFlagged).length,
    noCategory: drafts.filter(d => !d.product_type).length,
    totalValue: drafts.reduce((sum, d) => sum + (d.sellPrice > 0.01 ? d.sellPrice : 0), 0),
    totalCost: drafts.reduce((sum, d) => sum + (d.costPrice || 0), 0)
  }

  const getRowClass = (draft) => {
    const classes = []
    if (selected.has(draft.id)) classes.push('row-selected')
    if (pendingChanges[draft.id]) classes.push('row-modified')
    return classes.join(' ')
  }

  const getPriceClass = (draft) => {
    if (draft.isFlagged) return 'text-warning'
    if (draft.isUnpriced) return 'text-error'
    return 'text-success'
  }

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortDir('asc')
    }
  }

  const sortIndicator = (field) => sortBy === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''

  const pendingCount = Object.keys(pendingChanges).length

  const metrics = [
    { value: stats.total, label: 'Total Drafts', color: '' },
    { value: stats.priced, label: 'Priced', color: 'green' },
    { value: stats.unpriced, label: 'Unpriced', color: 'red' },
    { value: stats.flagged, label: 'Flagged', color: 'amber' },
    ...(stats.noCategory > 0 ? [{ value: stats.noCategory, label: 'No Category', color: 'purple' }] : []),
    { value: stats.totalValue, label: 'Total Value', color: 'green', isCurrency: true },
    { value: stats.totalCost, label: 'Total Cost', color: 'muted', isCurrency: true }
  ]

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="card" style={{ textAlign: 'center', marginTop: 'var(--space-8)' }}>
        <div style={{ fontSize: '48px', marginBottom: 'var(--space-4)' }}>⚠</div>
        <p className="text-muted" style={{ marginBottom: 'var(--space-4)' }}>{error}</p>
        <button onClick={fetchDrafts} className="btn btn-primary">Retry</button>
      </div>
    )
  }

  return (
    <div>
      {/* Pricing Results Modal */}
      {showPricingResults && (
        <PricingResultsModal
          results={pricingResultsData}
          onToggleSelection={handleTogglePricingResult}
          onApply={applyPricingChanges}
          onCancel={() => setShowPricingResults(false)}
          applying={pricing}
          progress={pricingProgress}
        />
      )}

      {/* Filter Panel */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="filter-section">
          <div className="filter-group filter-group-wide">
            <label className="filter-label">Search</label>
            <input
              type="text"
              placeholder="Search by title or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input"
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="form-input">
              <option value="all">All ({stats.total})</option>
              <option value="priced">Priced ({stats.priced})</option>
              <option value="unpriced">Unpriced ({stats.unpriced})</option>
              <option value="flagged">Flagged ({stats.flagged})</option>
              <option value="nocategory">No Category ({stats.noCategory})</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="filter-actions">
          <span className="text-sm text-muted">
            {drafts.length > 0 && `${filteredDrafts.length} of ${drafts.length} draft products`}
          </span>
          <div className="filter-actions-right">
            <button onClick={fetchDrafts} className="btn btn-secondary">
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Metrics Row */}
      <MetricsRow metrics={metrics} />

      {pricing && (
        <ProgressBar
          current={pricingProgress.current}
          total={pricingProgress.total}
          status={pricingProgress.status}
          onCancel={cancelPricing}
        />
      )}

      {/* Bulk Actions */}
      {filteredDrafts.length > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            <span className="text-sm" style={{ fontWeight: 500, color: 'var(--color-primary)' }}>
              {selected.size} selected
            </span>

            {pendingCount > 0 && (
              <button onClick={saveChanges} disabled={saving} className="btn btn-primary">
                {saving ? 'Saving...' : `Save Changes (${pendingCount})`}
              </button>
            )}

            <button onClick={priceSelected} disabled={selected.size === 0 || pricing} className="btn btn-secondary">
              Price Selected
            </button>
            <button
              onClick={activateSelected}
              disabled={selected.size === 0 || activating || pricing}
              className="btn btn-success"
            >
              {activating ? 'Activating...' : 'Activate Selected'}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {filteredDrafts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Draft Products</div>
            <div className="empty-state-description">
              {drafts.length === 0
                ? 'No draft products found in Shopify.'
                : 'Try adjusting your filters to see more products.'}
            </div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={filteredDrafts.length > 0 && filteredDrafts.every(d => selected.has(d.id))}
                    onChange={selectAllVisible}
                  />
                </th>
                <th style={{ width: 60 }}>Image</th>
                <th style={{ width: 50, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('photos')}>Photos{sortIndicator('photos')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('title')}>Title{sortIndicator('title')}</th>
                <th style={{ width: 180, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('type')}>Category{sortIndicator('type')}</th>
                <th style={{ width: 100, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('cost')}>Cost{sortIndicator('cost')}</th>
                <th style={{ width: 100, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('price')}>Price{sortIndicator('price')}</th>
                <th style={{ width: 100, textAlign: 'center' }}>Status</th>
                <th style={{ width: 120 }}>Search</th>
                <th style={{ width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrafts.map((draft) => (
                <tr key={draft.id} className={getRowClass(draft)}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(draft.id)}
                      onChange={() => toggleSelect(draft.id)}
                    />
                  </td>
                  <td>
                    {draft.image ? (
                      <img
                        src={draft.image}
                        alt=""
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                      />
                    ) : (
                      <div style={{ width: 48, height: 48, background: 'var(--color-gray-100)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-gray-400)', fontSize: 11 }}>
                        No img
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={draft.imageCount === 0 ? 'text-error' : draft.imageCount < 3 ? 'text-warning' : 'text-success'}>
                      {draft.imageCount}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <EditableCell
                        value={draft.title}
                        onChange={(newValue) => updateDraft(draft.id, 'title', newValue)}
                      />
                    </div>
                    <div className="text-sm text-muted" style={{ fontFamily: 'monospace', fontSize: 10 }}>
                      {draft.sku || draft.id}
                    </div>
                  </td>
                  <td>
                    <CategoryDropdown
                      value={draft.product_type}
                      onChange={(newValue) => updateDraft(draft.id, 'product_type', newValue)}
                    />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <EditablePriceCell
                      value={draft.costPrice}
                      onChange={(newValue) => updateDraft(draft.id, 'cost', newValue)}
                    />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <EditablePriceCell
                      value={draft.sellPrice}
                      onChange={(newValue) => updateDraft(draft.id, 'price', newValue)}
                      className={getPriceClass(draft)}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <DraftStatusBadge draft={draft} />
                  </td>
                  <td>
                    <SearchLinks title={draft.title} />
                  </td>
                  <td>
                    <a
                      href={`${SHOPIFY_ADMIN_URL}/products/${draft.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-secondary"
                    >
                      View
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Per-page selector */}
        {filteredDrafts.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
            <span className="text-sm text-muted">
              Showing {filteredDrafts.length} of {allFilteredDrafts.length} filtered ({drafts.length} total)
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="text-sm text-muted">Per page:</span>
              {[25, 50, 100, 200].map(num => (
                <button
                  key={num}
                  type="button"
                  className={`btn btn-sm ${limit === num ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLimit(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DraftsManager
